import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { startSessionAsync, startSessionWithAuthTokenAsync, endSessionAsync } from '../apis/session';
import { authorizeAsync, resendAuthorizationCodeAsync } from '../apis/authorization';
import { initialiseHttpService, setHttpServiceTargetOrigin } from '../utils/httpService';
import { useTenant } from './TenantProvider';
import { getUserEmailFromJwt, getUserNameFromJwt } from '../utils';

const AuthContext = createContext(null);

function useAuthHook() {
  const { widgetKey } = useTenant();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [startSessionError, setStartSessionError] = useState(null);

  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const [requireAuthorization, setRequireAuthorization] = useState(false);
  const [authorizationError, setAuthorizationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [targetOrigin, setTargetOrigin] = useState(undefined);

  const [token, setToken] = useState(null);
  // Set true after a successful silent-auth redemption so the welcome overlay can use a
  // different subtitle from the "resuming a previous chat" variant.
  const [silentAuthApplied, setSilentAuthApplied] = useState(false);

  // processMessage's closure is stable across renders (set up in useLayoutEffect with deps that
  // rarely change), so we read the current token/targetOrigin through refs to avoid stale state.
  const tokenRef = useRef(token);
  const targetOriginRef = useRef(undefined);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { targetOriginRef.current = targetOrigin; }, [targetOrigin]);

  const query = new URLSearchParams(window.location.search);

  useLayoutEffect(() => {
    const processMessage = async (event) => {
      //ignore the message from itself
      if (event.origin === window.location.origin) return;
      const data = event.data;
      if (!data) return;

      if (data.origin) {
        setTargetOrigin(data.origin);
        // Mirror to httpService so its out-of-React 401 interceptor can post-message with a
        // tightened targetOrigin instead of wildcard "*".
        setHttpServiceTargetOrigin(data.origin);
      }

      if (data.token !== null && data.token !== undefined) {
        await initialiseHttpService(data.token);
        setToken(data.token);
      }

      // Silent-auth redemption: host page has handed us a single-use widget auth token.
      // Skip if a ChatSession JWT is already in place (e.g. on hard reload after a previous
      // Phase 2) — that JWT is still valid and we don't want to consume an extra token row.
      if (data.kanisAuthToken && !tokenRef.current) {
        try {
          setIsLoading(true);
          const result = await startSessionWithAuthTokenAsync(widgetKey, data.kanisAuthToken);
          if (!result?.succeeded) {
            setStartSessionError(result?.errors?.[0] || 'Silent auth failed');
            return;
          }
          const newToken = result.result.token;
          await initialiseHttpService(newToken);
          setToken(newToken);
          setSilentAuthApplied(true);
          // Push the JWT back up to the host page so it can persist for cross-tab/reload survival.
          // Skip rather than fall back to "*" if targetOrigin somehow isn't set — never broadcast
          // a JWT to an unverified parent. In practice the loader's { origin } handshake always
          // lands before this branch runs, so the guard shouldn't trigger.
          if (!targetOriginRef.current) {
            // Breadcrumb only — behaviour unchanged. If this ever logs in the wild it means a
            // kanisAuthToken was redeemed before the parent's { origin } handshake landed, so the
            // freshly-minted JWT was not persisted to host localStorage (silent re-prompt on reload).
            console.debug('[AuthProvider] redeemed JWT not pushed to host: targetOrigin unknown (origin handshake had not arrived)');
            return;
          }
          window?.parent?.postMessage({ token: newToken }, targetOriginRef.current);
        } catch (e) {
          setError(e);
        } finally {
          setIsLoading(false);
        }
      }
    }

    window.addEventListener('message', processMessage, false);
    return () => { window.removeEventListener('message', processMessage); };
  }, [window, widgetKey]);


  useEffect(() => {
    if (!token) return;
    setUserName(getUserNameFromJwt(token));
    setUserEmail(getUserEmailFromJwt(token));
  }, [token]);

  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token]);

  const startSession = useCallback(async (userName, email, role = null) => {
    if (!widgetKey) return;

    try {
      setStartSessionError(null);

      const rolesQueryString = query.get('roles');

      const promptQueryString = query.get('prompt');

      const testRoles = (rolesQueryString != null) ? rolesQueryString.split(',') : [];

      const testPrompt = (promptQueryString != null) ? promptQueryString : null;

      const {succeeded, result, errors} = await startSessionAsync(widgetKey, userName, email, role, testRoles, testPrompt);

      if(!succeeded) {
        setStartSessionError(errors[0]);
        return;
      }

      var token = result.token;
      
      await initialiseHttpService(token);
      setToken(token);
      window?.parent?.postMessage({ token }, targetOrigin);
      if (token) {
        setUserName(getUserNameFromJwt(token));
        setUserEmail(getUserEmailFromJwt(token));
      }

      if (error) {
        setError('');
      }
    }
    catch (e) {
      setError(e);
    }
  }, [startSessionAsync, widgetKey, window, targetOrigin]);

  const endSession = useCallback(async () => {
    try {
      await endSessionAsync();
    }
    catch (e) {
      setError(e);
    }
  }, [endSessionAsync, window]);

  const logOut = useCallback(async () => {
    try {
      setIsLoading(true);
      await endSession();
      window?.parent?.postMessage({ token: null }, targetOrigin);
      setToken(null);
      setRequireAuthorization(false);
    }
    catch (e) {
      setError(e);
    }
    finally{
      setIsLoading(false);
    }
  }, [endSession, window, targetOrigin]);

  const authorize = useCallback(async (code) => {
    if (!code) return;
    
    try {
      setRequireAuthorization(false);

      const result = await authorizeAsync(code);

      if (result.authorized) {
        setAuthorizationError('');
        return;
      }
      setRequireAuthorization(true);
      setAuthorizationError(result.error);
    } catch (e) {
      setRequireAuthorization(true);
      setAuthorizationError(e.message);
    }
  }, [authorizeAsync]);

  const resendAuthorizationCode = useCallback(async () => {
    try {
      setIsLoading(true);
      await resendAuthorizationCodeAsync();
    } catch (e) {
      setAuthorizationError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [setRequireAuthorization]);

  const clearAuthorizationError = () => {
    setAuthorizationError('');
  }

  return {
    userName,
    userEmail,
    token,
    targetOrigin,
    silentAuthApplied,
    error,
    isAuthenticated,
    requireAuthorization,
    authorizationError,
    startSessionError,
    isLoading,
    setIsLoading,
    startSession,
    logOut,
    authorize,
    resendAuthorizationCode,
    setRequireAuthorization,
    clearAuthorizationError,
  };
}

export const useAuth = () => {
  const contextValue = useContext(AuthContext);
  if (contextValue === null) throw Error('AuthContext has not been Provided!');
  return contextValue;
};

export default function AuthProvider({ children }) {
  const auth = useAuthHook();
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};